/**
 * Default fraction digits when no Angular `digitsInfo` format is supplied.
 * Matches the host's universal fallback of `'0.2-2'` (see common-rate.ts / common-listener-helper.ts).
 */
export const DEFAULT_FRACTION_DIGITS = 2;
/**
 * Parses the max-fraction-digits out of an Angular `digitsInfo` string
 * (`'{minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}'`, e.g. `'0.2-2'`).
 * Returns {@link DEFAULT_FRACTION_DIGITS} when the format is missing or malformed.
 */
export function maxFractionDigits(format) {
  if (!format) {
    return DEFAULT_FRACTION_DIGITS;
  }
  const dashIndex = format.indexOf('-');
  const maxPart = dashIndex >= 0 ? format.slice(dashIndex + 1) : '';
  const parsed = Number.parseInt(maxPart, 10);
  return Number.isNaN(parsed) ? DEFAULT_FRACTION_DIGITS : parsed;
}
/**
 * Rounds half away from zero (1.005 -> 1.01, -1.005 -> -1.01), which is the behaviour
 * Angular's number formatter exhibits for `digitsInfo` rounding. Float representation caveats
 * apply identically on both sides, so FE parity is preserved.
 */
export function roundHalfAwayFromZero(value, fractionDigits) {
  if (!Number.isFinite(value)) {
    return value;
  }
  const factor = Math.pow(10, fractionDigits);
  const sign = value < 0 ? -1 : 1;
  return sign * Math.round(Math.abs(value) * factor) / factor;
}
/**
 * THE canonical numeric rounding utility of calc-core (T2.8, ratified 2026-08-19).
 *
 * Plain numeric, half away from zero, NO locale formatting or parsing. Rationale: the DB
 * always stores global-format numbers with `.` as the decimal separator, so bulkedit needs
 * no locale logic — the locale-aware format→parse pipeline (the FE's `autoCalcRound`) remains
 * a HOST-injected {@link RoundFn} in marketiq-frontend only.
 *
 * BE-4 (approved): bulkedit conversions round to 2 decimals — encoded here as the package
 * DEFAULT: when a host injects no `deps.round`, every rule rounds via `roundValue` with
 * `'0.2-2'` (through {@link defaultRound}). T3.1 note: bulkedit should therefore NOT inject
 * a `round` — the no-injection default IS the approved bulkedit behaviour.
 *
 * Non-finite inputs: `'keep'` passes them through untouched (matching
 * {@link roundHalfAwayFromZero} / the historical `defaultRound`); `'null'` returns `null`
 * (matching the finite-guarded null-on-≤0 pins of the space/HK-retail rules).
 */
export function roundValue(value, opts) {
  if ((opts?.nonPositive ?? 'keep') === 'null' && !(Number.isFinite(value) && value > 0)) {
    return null;
  }
  return roundHalfAwayFromZero(value, maxFractionDigits(opts?.format));
}
/**
 * The fallback {@link RoundFn} used when the host injects none (Node / unit tests).
 * Rounds to 2 decimals — the codebase's universal default precision. Since T2.8 this is
 * {@link roundValue} with its defaults (`format: '0.2-2'`, `nonPositive: 'keep'`) —
 * byte-identical numeric results to the original `roundHalfAwayFromZero(v, 2)`.
 *
 * The Angular adapter overrides this with the real locale-aware `formatNumber` pipeline,
 * so production FE values are byte-identical to today's; this default only governs
 * environments without that pipeline (Node / bulkedit / unit tests).
 */
export const defaultRound = value => roundValue(value);
